/**
 * P&L Reconciliation Grid
 * =======================
 * Year-at-a-glance table showing all P&L line items with reconciliation status.
 *
 * Features:
 * - Multiple years display
 * - Status badges (✓ Reconciled, ⚠️ In Review, ○ Not Started)
 * - Click cell to drill down into transactions
 * - Catch-all item highlighting (needs review)
 */

import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Circle, AlertTriangle, Clock } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { formatCurrency } from '@/utils/financialFormat';
import { CARD_CHARGES_NOTE } from './cardChargesLabel';

/** Backend-computed aggregates from pl_aggregate_service.compute_pl_grid_aggregates */
interface PLGridYearAggregates {
  /** Annual total per line item: { section: { line_item: float } } */
  row_totals: Record<string, Record<string, number>>;
  /** Sum of all line items per section per month: { section: [12 floats] } */
  section_month_totals: Record<string, number[]>;
  /** Annual section total: { section: float } */
  section_annual_totals: Record<string, number>;
  /** Sum of all section annual totals (expenses already negative) */
  net_income: number;
}

interface PLReconciliationGridProps {
  plGridData?: {
    report_id: string;
    years: number[];
    pl_structure: any;
    pl_grid: any;
    reconciliation_status: Record<string, string>;
    catch_all_items: Array<[string, string]>;
    /** Where the grid came from (reconciliation.py pl-grid): transactions (incl. card rows) | pl_summary | none. */
    data_source?: 'transactions' | 'pl_summary' | 'none';
    /** Pre-computed aggregates keyed by year string, e.g. "2022". */
    aggregates?: Record<string, PLGridYearAggregates>;
  };
  onCellClick: (year: number, month: number, section: string, lineItem: string) => void;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'reconciled':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
          <CheckCircle size={12} style={{ color: 'var(--color-text-secondary)' }} />
          Reconciled
        </span>
      );
    case 'in_review':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
          <Clock size={12} />
          In Review
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--color-text-dim)' }}>
          <Circle size={12} />
          Not Started
        </span>
      );
  }
};

export default function PLReconciliationGrid({ plGridData, onCellClick }: PLReconciliationGridProps) {
  const years = plGridData?.years ?? [];
  const [selectedYear, setSelectedYear] = useState<number>(years[years.length - 1] ?? 2023);

  if (!plGridData || !plGridData.years || plGridData.years.length === 0) {
    return (
      <div
        className="rounded-[2px] p-6 flex items-start gap-3"
        style={{
          backgroundColor: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border)',
        }}
        data-testid="pl-grid-unavailable"
      >
        <AlertCircle size={18} style={{ color: 'var(--color-text-muted)', marginTop: 2, flexShrink: 0 }} />
        <div>
          <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
            P&amp;L reconciliation grid unavailable
          </p>
          <p className="cs-meta mt-1" style={{ color: 'var(--color-text-muted)' }}>
            No reconciliation data has been generated for this case yet.
          </p>
        </div>
      </div>
    );
  }

  const { pl_structure, pl_grid, reconciliation_status, catch_all_items } = plGridData;

  // Check if a line item is a catch-all
  const isCatchAll = (section: string, lineItem: string): boolean => {
    return catch_all_items.some(([s, li]) => s === section && li === lineItem);
  };

  // Get reconciliation status for a cell
  const getCellStatus = (year: number, month: number, lineItem: string): string => {
    const cellId = `${year}-${String(month).padStart(2, '0')}-${lineItem.replace(/ /g, '_')}`;
    return reconciliation_status[cellId] || 'not_started';
  };

  // Get cell CSS classes (non-color layout/behavior only)
  const getCellClasses = (value: number): string => {
    const baseClasses = 'px-3 py-2 text-right cursor-pointer transition-colors';
    const valueClasses = value === 0 ? '' : 'font-medium';

    return `${baseClasses} ${valueClasses}`.trim();
  };

  // Get cell color styles (token-based)
  const getCellStyle = (section: string, lineItem: string, value: number): React.CSSProperties => {
    return {
      color: value === 0 ? 'var(--color-text-dim)' : 'var(--color-text-primary)',
    };
  };

  if (!pl_grid[selectedYear] && !pl_grid[String(selectedYear)]) {
    return (
      <div className="rounded-[2px] shadow p-6" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>No data available for {selectedYear}</p>
      </div>
    );
  }

  // Handle both string and number keys in pl_grid
  const yearData = pl_grid[selectedYear] || pl_grid[String(selectedYear)];

  // Backend-computed aggregates for the selected year (preferred over JS math).
  const yearAggs: PLGridYearAggregates | undefined =
    plGridData.aggregates?.[String(selectedYear)];

  if (!yearData || typeof yearData !== 'object') {
    return (
      <div className="rounded-[2px] shadow p-6" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>No P&L data available for {selectedYear}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="rounded-[2px] shadow p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center justify-between">
          <h2 className="font-normal" style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: '20px', color: 'var(--color-text-primary)' }}>Year-at-a-Glance P&L Reconciliation</h2>
          <div className="flex rounded-[2px] overflow-hidden border" style={{ borderColor: 'var(--color-border)' }}>
            {plGridData.years.map(year => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className="px-4 py-2 font-medium transition-colors"
                style={
                  selectedYear === year
                    ? { 
                        backgroundColor: 'var(--color-bg-elevated)', 
                        color: 'var(--color-text-secondary)',
                        borderBottom: '2px solid var(--color-brand)'
                      }
                    : { 
                        backgroundColor: 'transparent', 
                        color: 'var(--color-text-dim)' 
                      }
                }
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          <span className="flex items-center gap-1">
            <div style={{ width: 8, height: 8, backgroundColor: 'var(--color-brand)', borderRadius: 2 }}></div>
            = Catch-All (Needs Review)
          </span>
          <StatusBadge status="reconciled" />
          <StatusBadge status="in_review" />
          <StatusBadge status="not_started" />
        </div>
        {plGridData.data_source === 'transactions' && (
          <p
            data-testid="pl-grid-card-note"
            className="mt-2 text-xs"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Where credit card statements are uploaded, their rows are {CARD_CHARGES_NOTE}.
          </p>
        )}
      </div>

      {/* P&L Grid */}
      <div className="rounded-[2px] shadow overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider sticky left-0 z-10" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-elevated)' }}>
                  Category
                </th>
                {MONTH_NAMES.map((month, idx) => (
                  <th key={month} className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                    {month}
                  </th>
                ))}
                <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-hover)' }}>
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {/* Render each section */}
              {Object.entries(yearData).map(([section, lineItems]: [string, any]) => (
                <React.Fragment key={section}>
                  {/* Section Header */}
                  <tr style={{ backgroundColor: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}>
                    <td
                      colSpan={14}
                      className="px-4 py-3 sticky left-0"
                      style={{ 
                        color: 'var(--color-text-muted)', 
                        backgroundColor: 'var(--color-bg-elevated)',
                        fontFamily: "'Outfit', system-ui, sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em'
                      }}
                    >
                      {section}
                    </td>
                  </tr>

                  {/* Line Items */}
                  {Object.entries(lineItems).map(([lineItem, monthlyValues]: [string, any]) => {
                    const values = Array.isArray(monthlyValues) ? monthlyValues : [];
                    // Prefer backend-computed annual total; fall back to JS sum for deploy-order safety.
                    const total =
                      yearAggs?.row_totals?.[section]?.[lineItem]
                      ?? values.reduce((sum, val) => sum + (val || 0), 0);
                    // Row-level status badge (K3 P0): same status lookup + gating
                    // condition that used to render inside the JAN month cell,
                    // just relocated to the CATEGORY column so JAN no longer
                    // breaks the row's single-line baseline vs FEB-DEC.
                    const rowStatus = getCellStatus(selectedYear, 1, lineItem);
                    const janValue = values[0] ?? 0;

                    return (
                      <tr key={`${section}-${lineItem}`} className="border-b" style={{ borderColor: 'var(--color-border-faint)' }}>
                        <td className="px-4 py-3 text-sm sticky left-0 whitespace-nowrap" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-surface)' }}>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center">
                              {isCatchAll(section, lineItem) && (
                                <div
                                  style={{
                                    width: 8,
                                    height: 8,
                                    backgroundColor: 'var(--color-brand)',
                                    borderRadius: 2,
                                    marginRight: 6,
                                    flexShrink: 0
                                  }}
                                />
                              )}
                              <span className="font-medium">{lineItem}</span>
                            </div>
                            {janValue !== 0 && rowStatus !== 'not_started' && (
                              <div className="scale-75 origin-left">
                                <StatusBadge status={rowStatus} />
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Monthly Values — single line per cell: just the
                            figure, right-aligned via the td's own text-right
                            (getCellClasses), matching the Total cell's plain
                            span-in-text-right-td alignment logic below. */}
                        {values.map((value: number, monthIndex: number) => (
                          <td
                            key={monthIndex}
                            className={getCellClasses(value)}
                            style={getCellStyle(section, lineItem, value)}
                            onClick={() => value !== 0 && onCellClick(selectedYear, monthIndex + 1, section, lineItem)}
                            title={`Click to reconcile ${lineItem} for ${MONTH_NAMES[monthIndex]} ${selectedYear}`}
                          >
                            <span style={{
                              fontFamily: "'DM Mono', ui-monospace, monospace",
                              fontSize: '13px',
                              color: value < 0 ? 'var(--color-blocker)' : (value === 0 ? 'var(--color-text-dim)' : 'var(--color-text-secondary)')
                            }}>
                              {formatCurrency(value)}
                            </span>
                          </td>
                        ))}

                        {/* Total */}
                        <td className="px-3 py-3 text-right" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-elevated)', fontFamily: "'DM Mono', ui-monospace, monospace", fontSize: '13px', fontWeight: 600 }}>
                          <FinancialValue value={total} />
                        </td>
                      </tr>
                    );
                  })}

                  {/* Section Total Row */}
                  {section !== 'OPERATING EXPENSES' && (
                    <tr className="border-t" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                      <td
                        className="px-4 py-3 sticky left-0 whitespace-nowrap"
                        style={{
                          color: 'var(--color-text-primary)',
                          backgroundColor: 'var(--color-bg-elevated)',
                          fontFamily: "'Outfit', system-ui, sans-serif", // design-law-allow: mirrors the Section Header cell's existing small-caps treatment above — reused pattern, not new debt.
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                        }}
                      >
                        Total {section}
                      </td>
                      {MONTH_NAMES.map((_, monthIndex) => {
                        // Prefer backend-computed monthly section subtotal; fall back to JS.
                        const monthTotal =
                          yearAggs?.section_month_totals?.[section]?.[monthIndex]
                          ?? Object.values(lineItems).reduce<number>(
                               (sum, monthlyValues: any) => sum + (monthlyValues[monthIndex] || 0),
                               0
                             );
                        return (
                          <td key={monthIndex} className="px-3 py-3 text-right" style={{ color: 'var(--color-text-primary)', fontFamily: "'DM Mono', ui-monospace, monospace", fontSize: '13px', fontWeight: 600 }}>
                            <FinancialValue value={monthTotal} />
                          </td>
                        );
                      })}
                      <td className="px-3 py-3 text-right" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-hover)', fontFamily: "'DM Mono', ui-monospace, monospace", fontSize: '13px', fontWeight: 600 }}>
                        <FinancialValue value={
                          // Prefer backend-computed annual section total; fall back to JS.
                          yearAggs?.section_annual_totals?.[section]
                          ?? Object.values(lineItems).reduce<number>(
                               (sum, monthlyValues: any) =>
                                 sum + monthlyValues.reduce((s: number, v: number) => s + (v || 0), 0),
                               0
                             )
                        } />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Instructions */}
      <div className="rounded-[2px] p-4 border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
        <h3 className="mb-2" style={{ 
          fontFamily: "'Outfit', system-ui, sans-serif",
          fontSize: '11px', 
          fontWeight: 600, 
          textTransform: 'uppercase', 
          letterSpacing: '0.08em',
          color: 'var(--color-text-muted)' 
        }}>How to Use</h3>
        <ul className="space-y-1" style={{ color: 'var(--color-text-muted)', fontSize: '13px', fontFamily: "'Outfit', system-ui, sans-serif" }}>
          <li style={{ display: 'flex', gap: '0.5rem' }}>
            <span style={{ color: 'var(--color-text-dim)' }}>—</span>
            <span><strong style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>Click any cell</strong> with a dollar amount to drill down into transactions</span>
          </li>
          <li style={{ display: 'flex', gap: '0.5rem' }}>
            <span style={{ color: 'var(--color-text-dim)' }}>—</span>
            <span><strong style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>Rows flagged with the ■ catch-all marker</strong> are catch-all categories (e.g., "Other Operating Expenses") that may contain misclassified transactions</span>
          </li>
          <li style={{ display: 'flex', gap: '0.5rem' }}>
            <span style={{ color: 'var(--color-text-dim)' }}>—</span>
            <span><strong style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>Status badges</strong> show reconciliation progress (Not Started → In Review → Reconciled)</span>
          </li>
          <li style={{ display: 'flex', gap: '0.5rem' }}>
            <span style={{ color: 'var(--color-text-dim)' }}>—</span>
            <span>Review and sign off each cell to mark as "Reconciled"</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
